/**
 * hooks/useTranslatr.tsx - React Hook for Translatr UI
 * 
 * CHANGE HISTORY:
 * - 251109_0814 [pending] danielbeer: Updated to use Pluggr for plugin management
 * - 251109_0803 [pending] danielbeer: Created React hook using Translatr core functions
 * 
 * React hook that provides UI state management for Translatr conversions.
 * Uses core Translatr functions for all conversions - no duplication!
 * Integrates with Pluggr for plugin management.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { 
  csvToJson, 
  jsonToCsv, 
  xmlToJson, 
  jsonToXml,
  markdownToJson,
  jsonToMarkdown,
  vcardToJson,
  jsonToVcard,
  autoParse
} from '../../compatibility';
import type {
  DataFormat,
  DisplayMode,
  PanelConfig,
  TransformResult,
  FormatPlugin,
  TranslatrState,
  TranslatrActions
} from '../types';

export interface UseTranslatrOptions {
  initialSource?: string;
  initialSourceFormat?: DataFormat;
  initialTargetFormat?: DataFormat;
  onTransform?: (result: TransformResult) => void;
  onChange?: (source: string, target: string) => void;
  autoTransform?: boolean;
  debounceDelay?: number;
  plugins?: FormatPlugin[];
}

export interface UseTranslatrReturn extends TranslatrState, TranslatrActions {}

/**
 * Main hook for Translatr UI functionality
 * Uses core Translatr functions for all conversions
 */
export function useTranslatr(options: UseTranslatrOptions): UseTranslatrReturn {
  const {
    initialSource = '',
    initialSourceFormat = 'auto',
    initialTargetFormat = 'json',
    onTransform,
    onChange,
    autoTransform = false,
    debounceDelay = 500,
    plugins: customPlugins = []
  } = options;

  // Initialize plugins
  const [plugins] = useState<Map<DataFormat, FormatPlugin>>(() => {
    const map = new Map<DataFormat, FormatPlugin>();
    
    // Register custom plugins
    customPlugins.forEach(plugin => {
      map.set(plugin.format, plugin);
    });
    
    return map;
  });

  // State
  const [source, setSource] = useState<PanelConfig>({
    format: initialSourceFormat,
    displayMode: 'raw',
    content: initialSource
  });

  const [target, setTarget] = useState<PanelConfig>({
    format: initialTargetFormat,
    displayMode: 'formatted',
    content: ''
  });

  const [isTransforming, setIsTransforming] = useState(false);
  const [lastResult, setLastResult] = useState<TransformResult | undefined>();
  const [showMappr, setShowMappr] = useState(false);
  const [sourceSchema, setSourceSchema] = useState<any[]>([]);
  const [targetSchema, setTargetSchema] = useState<any[]>([]);

  // Debounce timer
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Detect format using Translatr's autoParse
  const detectFormat = useCallback(async (content: string): Promise<DataFormat> => {
    if (!content.trim()) return 'text';

    try {
      const result = await autoParse(content);
      // autoParse returns { format, data }
      return (result as any).format || 'text';
    } catch {
      return 'text';
    }
  }, []);

  // Transform data using Translatr core functions
  const transform = useCallback(async (): Promise<TransformResult> => {
    setIsTransforming(true);
    const startTime = Date.now();

    try {
      let sourceFormat = source.format;
      
      // Auto-detect if needed
      if (sourceFormat === 'auto') {
        sourceFormat = await detectFormat(source.content);
        setSource(prev => ({
          ...prev,
          detectedFormat: sourceFormat,
          confidence: 85
        }));
      }

      // Get source plugin
      const sourcePlugin = plugins.get(sourceFormat);
      
      // Parse source using Translatr or plugin
      let parsedData: any;
      if (sourcePlugin) {
        parsedData = await sourcePlugin.parse(source.content);
      } else {
        // Use Translatr core functions
        if (sourceFormat === 'json') {
          parsedData = JSON.parse(source.content);
        } else if (sourceFormat === 'csv') {
          parsedData = await csvToJson(source.content);
        } else if (sourceFormat === 'xml') {
          parsedData = await xmlToJson(source.content);
        } else if (sourceFormat === 'markdown') {
          parsedData = await markdownToJson(source.content);
        } else if (sourceFormat === 'vcard') {
          parsedData = await vcardToJson(source.content);
        } else {
          parsedData = source.content;
        }
      }

      // Get target plugin
      const targetPlugin = plugins.get(target.format);
      
      // Serialize to target format using Translatr or plugin
      let output: string;
      if (targetPlugin) {
        output = await targetPlugin.serialize(parsedData);
      } else {
        // Use Translatr core functions
        if (target.format === 'json') {
          output = JSON.stringify(parsedData, null, 2);
        } else if (target.format === 'csv') {
          output = await jsonToCsv(parsedData);
        } else if (target.format === 'xml') {
          output = await jsonToXml(parsedData, 'root');
        } else if (target.format === 'markdown') {
          output = await jsonToMarkdown(parsedData);
        } else if (target.format === 'vcard') {
          output = await jsonToVcard(parsedData);
        } else {
          output = String(parsedData);
        }
      }

      const transformTime = Date.now() - startTime;
      
      const result: TransformResult = {
        output,
        format: target.format,
        success: true,
        metadata: {
          recordCount: Array.isArray(parsedData) ? parsedData.length : 1,
          byteSize: output.length,
          transformTime
        }
      };

      setTarget(prev => ({
        ...prev,
        content: output,
        parsedData
      }));

      setLastResult(result);
      
      if (onTransform) {
        onTransform(result);
      }

      return result;
    } catch (error: any) {
      const result: TransformResult = {
        output: '',
        format: target.format,
        success: false,
        error: error.message || 'Transformation failed'
      };

      setLastResult(result);
      return result;
    } finally {
      setIsTransforming(false);
    }
  }, [source, target, plugins, detectFormat, onTransform]);

  // Set source content
  const setSourceContent = useCallback((content: string) => {
    setSource(prev => ({ ...prev, content }));
    
    if (onChange) {
      onChange(content, target.content);
    }

    // Auto-transform if enabled
    if (autoTransform) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
      debounceTimer.current = setTimeout(() => {
        transform();
      }, debounceDelay);
    }
  }, [target.content, onChange, autoTransform, debounceDelay, transform]);

  // Set source format
  const setSourceFormat = useCallback((format: DataFormat) => {
    setSource(prev => ({ ...prev, format }));
  }, []);

  // Set source display mode
  const setSourceDisplayMode = useCallback((mode: DisplayMode) => {
    setSource(prev => ({ ...prev, displayMode: mode }));
  }, []);

  // Set target format
  const setTargetFormat = useCallback((format: DataFormat) => {
    setTarget(prev => ({ ...prev, format }));
  }, []);

  // Set target display mode
  const setTargetDisplayMode = useCallback((mode: DisplayMode) => {
    setTarget(prev => ({ ...prev, displayMode: mode }));
  }, []);

  // Open Mappr
  const openMappr = useCallback(() => {
    // Extract schemas
    if (source.parsedData) {
      setSourceSchema(extractSchema(source.parsedData));
    }
    if (target.parsedData) {
      setTargetSchema(extractSchema(target.parsedData));
    }
    setShowMappr(true);
  }, [source.parsedData, target.parsedData]);

  // Close Mappr
  const closeMappr = useCallback(() => {
    setShowMappr(false);
  }, []);

  // Apply mappings from Mappr
  const applyMappings = useCallback((mappings: any[]) => {
    // TODO: Apply field mappings to transform
    console.log('Applying mappings:', mappings);
    closeMappr();
  }, [closeMappr]);

  // Load sample data
  const loadSample = useCallback((sampleData: string) => {
    setSourceContent(sampleData);
  }, [setSourceContent]);

  // Clear all
  const clear = useCallback(() => {
    setSource({
      format: 'auto',
      displayMode: 'raw',
      content: ''
    });
    setTarget({
      format: 'json',
      displayMode: 'formatted',
      content: ''
    });
    setLastResult(undefined);
  }, []);

  // Copy to clipboard
  const copyToClipboard = useCallback(async (content: string) => {
    try {
      await navigator.clipboard.writeText(content);
      return true;
    } catch {
      return false;
    }
  }, []);

  // Auto-transform on mount if initial source provided
  useEffect(() => {
    if (initialSource && autoTransform) {
      transform();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-transform when source format changes (and content exists)
  useEffect(() => {
    if (source.content && autoTransform) {
      const timer = setTimeout(() => {
        transform();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [source.format]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-transform when target format changes (and content exists)
  useEffect(() => {
    if (source.content && autoTransform) {
      const timer = setTimeout(() => {
        transform();
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [target.format]); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // State
    source,
    target,
    isTransforming,
    lastResult,
    showMappr,
    sourceSchema,
    targetSchema,
    
    // Actions
    transform,
    setSourceContent,
    setSourceFormat,
    setSourceDisplayMode,
    setTargetFormat,
    setTargetDisplayMode,
    openMappr,
    closeMappr,
    applyMappings,
    loadSample,
    clear,
    copyToClipboard
  };
}

// Helper function
function extractSchema(data: any): any[] {
  if (!data) return [];
  
  const sample = Array.isArray(data) ? data[0] : data;
  if (!sample || typeof sample !== 'object') return [];
  
  return Object.keys(sample).map((key, idx) => ({
    id: `field_${idx}`,
    name: key,
    type: typeof sample[key],
    sample: sample[key]
  }));
}
